import { TranslateModule } from '@ngx-translate/core';
import { IFlatModel } from '@services/api/flat/flat-model.interface';
import { ModelFormatterService } from '@services/formatter/model-formatter.service';
import { UiSelectItem } from '@services/util/select-item-loader.service';
import { SelectModule } from 'primeng/select';
import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  EventEmitter,
  inject,
  input,
  OnChanges,
  OnInit,
  Output,
  signal,
  SimpleChanges
} from '@angular/core';
import { FormsModule } from '@angular/forms';

/**
 * Component for handling parking place field in parking request form
 * Manages both editable (create mode) and readonly (edit mode) states
 */
@Component({
  selector: 'app-parking-request-parking-place-field',
  standalone: true,
  imports: [CommonModule, FormsModule, TranslateModule, SelectModule],
  template: `
    <div class="field">
      <label class="mb-2 block font-medium">{{ 'parking.form.parkingPlace' | translate }}</label>

      <p-select
        [options]="displayOptions()"
        [(ngModel)]="parkingPlaceValue"
        name="parkingPlace"
        [ngModelOptions]="{ standalone: true }"
        [disabled]="readonly()"
        (ngModelChange)="onParkingPlaceChange($event)"
        optionLabel="explicitTitle"
        optionValue="value"
        [showClear]="!readonly()"
        placeholder="{{ 'parking.form.selectParkingPlace' | translate }}"
        styleClass="w-full"
      >
        <ng-template pTemplate="empty">
          <div class="p-2 text-center">{{ 'parking.form.noParkingPlacesFound' | translate }}</div>
        </ng-template>
        <ng-template
          pTemplate="selectedItem"
          let-parkingPlace
        >
          @if (parkingPlace) {
            <div class="flex items-center">
              <span>{{ parkingPlace.explicitTitle }}</span>
            </div>
          } @else {
            <span class="text-gray-500">{{ 'parking.form.noParkingPlaceSelected' | translate }}</span>
          }
        </ng-template>
      </p-select>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ParkingRequestParkingPlaceFieldComponent implements OnInit, OnChanges {
  // Services
  private readonly modelFormatterService = inject(ModelFormatterService);

  // Input signals
  readonly = input<boolean>(false);
  parkingPlaceOptions = input<UiSelectItem<IFlatModel>[]>([]);
  initialValue = input<string | null>(null);
  selectedParkingPlaceObject = input<IFlatModel | null>(null);

  // Output events
  @Output() parkingPlaceChange = new EventEmitter<string | null>();

  // Component state
  parkingPlaceValue: string | null = null;
  selectedParkingPlace = signal<UiSelectItem<IFlatModel> | null>(null);

  /**
   * Computed property that includes fallback parking place in options if needed
   */
  displayOptions = computed(() => {
    const regularOptions = this.parkingPlaceOptions();
    const fallbackParkingPlace = this.selectedParkingPlace();

    // If we have a fallback parking place that's not in regular options, add it
    if (fallbackParkingPlace && !regularOptions.find((pp) => pp.value === fallbackParkingPlace.value)) {
      return [fallbackParkingPlace, ...regularOptions];
    }

    return regularOptions;
  });

  ngOnInit(): void {
    this.updateFromInitialValue();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['initialValue'] || changes['selectedParkingPlaceObject'] || changes['parkingPlaceOptions']) {
      this.updateFromInitialValue();
    }
  }

  /**
   * Update component state from initialValue input
   */
  private updateFromInitialValue(): void {
    const initialValue = this.initialValue();
    const parkingPlaceObject = this.selectedParkingPlaceObject();

    if (initialValue && initialValue !== this.parkingPlaceValue) {
      this.parkingPlaceValue = initialValue;

      // First, try to find the value in available SILO options (priority)
      this.updateSelectedParkingPlace();

      // Only use fallback object if not found in SILO options
      if (!this.selectedParkingPlace() && parkingPlaceObject && parkingPlaceObject['@id'] === initialValue) {
        const formattedTitle = this.modelFormatterService.getFormattedParkingSpace(parkingPlaceObject);
        const selectItem: UiSelectItem<IFlatModel> = {
          value: parkingPlaceObject['@id'],
          explicitTitle: formattedTitle,
          model: parkingPlaceObject
        };
        this.selectedParkingPlace.set(selectItem);
      }
    }
  }

  /**
   * Handle parking place change from UI
   */
  onParkingPlaceChange(parkingPlaceIri: string | null): void {
    this.parkingPlaceValue = parkingPlaceIri;
    this.updateSelectedParkingPlace();
    this.parkingPlaceChange.emit(parkingPlaceIri);
  }

  /**
   * Update selected parking place object for display
   */
  private updateSelectedParkingPlace(): void {
    if (this.parkingPlaceValue) {
      const parkingPlace = this.parkingPlaceOptions().find((pp) => pp.value === this.parkingPlaceValue);

      if (parkingPlace) {
        this.selectedParkingPlace.set(parkingPlace);
      } else {
        this.selectedParkingPlace.set(null);
      }
    } else {
      this.selectedParkingPlace.set(null);
    }
  }

  /**
   * Update parking place value from parent (for programmatic changes)
   */
  updateValue(value: string | null): void {
    this.parkingPlaceValue = value;
    this.updateSelectedParkingPlace();
  }

  /**
   * Update parking place options and refresh selected parking place
   */
  updateOptions(): void {
    this.updateSelectedParkingPlace();
  }

  /**
   * Set custom parking place data for readonly display when not in options list
   */
  setCustomParkingPlace(flatModel: IFlatModel): void {
    if (this.readonly()) {
      const customItem: UiSelectItem<IFlatModel> = {
        value: flatModel['@id'] || '',
        explicitTitle: flatModel.name || 'Unknown Parking Place',
        model: flatModel
      };
      this.selectedParkingPlace.set(customItem);
    }
  }
}
